import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSettings, putSettings, getRecomputeStatus } from "@/lib/api";
import { JobProgress } from "@/components/JobProgress";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/lib/auth";
import { Lock } from "lucide-react";
import { toast } from "sonner";

const NUM_FIELDS = [
  ["safety_stock_months", "Safety Stock (months)", "Extra cover added on top of lead-time demand"],
  ["target_stock_cover", "Target Stock Cover (months)", "Target months of cover for planning"],
  ["overstock_threshold", "Overstock Cover Threshold (months)", "SOH cover above this flags Overstocked"],
  ["critical_threshold", "Critical Threshold (fraction of LT demand)", "SOH below this share of lead-time demand is Critical"],
  ["default_lead_time", "Default Lead Time (where missing)", "Used when no source or manual lead time exists"],
];
const TOGGLES = [
  ["include_open_po", "Include Open PO in Projected Supply"],
  ["include_in_receipt", "Include In Receipt in Projected Supply"],
  ["include_in_transit", "Include In Transit in Projected Supply"],
];

export default function Settings() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { data } = useQuery({ queryKey: ["settings"], queryFn: getSettings });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recompute, setRecompute] = useState(null); // {progress, stage}
  useEffect(() => { if (data) setForm(data); }, [data]);
  if (!form) return null;
  const set = (k, v) => isAdmin && setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setRecompute(null);
    try {
      const payload = { ...form };
      NUM_FIELDS.forEach(([k]) => (payload[k] = Number(payload[k])));
      const res = await putSettings(payload);
      if (res.job_id) {
        setRecompute({ progress: 0, stage: "Queued…" });
        const started = Date.now();
        while (true) {
          await new Promise((r) => setTimeout(r, 1500));
          const st = await getRecomputeStatus(res.job_id);
          setRecompute({ progress: st.progress || 0, stage: st.stage || "Recomputing…" });
          if (st.status === "done") { toast.success("Settings saved — recommendations recalculated"); qc.invalidateQueries(); break; }
          if (st.status === "failed") { toast.error("Recompute failed — settings saved but data may be stale"); break; }
          if (Date.now() - started > 10 * 60 * 1000) { toast.error("Recompute taking longer than expected"); break; }
        }
      } else {
        toast.success("Settings saved");
        qc.invalidateQueries();
      }
    } catch (e) { toast.error("Could not save settings"); } finally { setSaving(false); setRecompute(null); }
  };

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      <div>
        <div className="label-eyebrow">Configuration</div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Settings</h1>
        <p className="text-sm text-slate-500 mt-0.5">Changes recompute all inventory statuses and PO recommendations. Settings persist across uploads.</p>
      </div>

      {!isAdmin && (
        <div className="flex items-center gap-2 text-[13px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <Lock className="w-4 h-4" /> Read-only account — settings can be viewed but not changed.
        </div>
      )}

      <div className="kcard p-5 space-y-4">
        <div className="label-eyebrow">Planning Parameters</div>
        {NUM_FIELDS.map(([k, label, help]) => (
          <div key={k} className="flex items-center justify-between gap-4 py-1">
            <div><div className="text-[13px] font-medium text-slate-800">{label}</div><div className="text-[12px] text-slate-400">{help}</div></div>
            <input value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} className="w-28 h-9 rounded-md border border-[var(--line)] px-3 text-[13px] num text-right focus:border-slate-400 outline-none" />
          </div>
        ))}
        <div className="flex items-center justify-between gap-4 py-1">
          <div><div className="text-[13px] font-medium text-slate-800">Lead-Time Unit</div><div className="text-[12px] text-slate-400">Unit used for lead-time values</div></div>
          <Select value={form.lead_time_unit} onValueChange={(v) => set("lead_time_unit", v)}>
            <SelectTrigger className="w-40 h-9 text-[13px]" data-testid="setting-lead_time_unit"><SelectValue /></SelectTrigger>
            <SelectContent>{["months", "weeks", "days"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="flex items-center justify-between gap-4 py-1">
          <div><div className="text-[13px] font-medium text-slate-800">PO Recommendation Method</div><div className="text-[12px] text-slate-400">Default: Lead Time + Safety Stock</div></div>
          <Select value={form.po_method} onValueChange={(v) => set("po_method", v)}>
            <SelectTrigger className="w-56 h-9 text-[13px]" data-testid="setting-po_method"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="lead_time_safety_stock">Lead Time + Safety Stock</SelectItem><SelectItem value="target_cover">Target Stock Cover</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="kcard p-5 space-y-3">
        <div className="label-eyebrow">Projected Supply Components</div>
        {TOGGLES.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between py-1">
            <div className="text-[13px] font-medium text-slate-800">{label}</div>
            <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`setting-${k}`} />
          </div>
        ))}
      </div>

      {recompute && (
        <JobProgress
          progress={recompute.progress}
          stage={recompute.stage === "Done" ? "Recompute complete" : `Recomputing inventory… ${recompute.stage}`}
          hint="Rebuilding every item's status and PO recommendation with the new settings."
          testid="recompute-progress"
        />
      )}

      <button onClick={save} disabled={saving || !isAdmin} data-testid="save-settings" className="h-10 px-6 rounded-md text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: "var(--kcsa-red)" }}>{saving ? "Saving & recalculating…" : "Save Settings"}</button>
    </div>
  );
}
